import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import {
  FaArrowRight,
  FaMapMarkerAlt,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaClipboardList,
} from "react-icons/fa";
import "../Styles/HomeHero.css";

/**
 * The departure-board line under the headline.
 *
 * The old hero rotated the h1 itself, which meant the page's most important
/**
 * The departure-board line under the headline.
 */
const NOW_ADVISING = [
  "Canada PR & Express Entry",
  "UK Masters & Graduate Route",
  "German Blue Card & Opportunity Card",
  "Australia Subclass 189 / 190",
  "US F-1 & STEM OPT",
  "UAE Golden Visa",
];

const ROTATING_SERVICES = [
  "overseas education",
  "permanent residency",
  "global careers",
  "global migration",
];

const TRUST = [
  { figure: "98%", label: "Visa success rate" },
  { figure: "ICCRC", label: "Certified consultants" },
  { figure: "500+", label: "Partner universities" },
  { figure: "1989", label: "Advising since" },
];

export default function HomeHero({ onToast }) {
  const [rotating, setRotating] = useState(0);
  const [serviceIndex, setServiceIndex] = useState(0);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
  });

  useEffect(() => {
    const timer = setInterval(
      () => setRotating((i) => (i + 1) % NOW_ADVISING.length),
      2800
    );
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const serviceTimer = setInterval(
      () => setServiceIndex((i) => (i + 1) % ROTATING_SERVICES.length),
      3200
    );
    return () => clearInterval(serviceTimer);
  }, []);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || "https://veabroad.com"}/api/submit/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...formData, category: "Home Assessment", source: "Home hero" }),
        }
      );
      await res.json().catch(() => ({}));
      onToast?.({
        message:
          "Assessment request received. A counsellor will call you within 24 hours.",
        type: "success",
      });
      setFormData({ name: "", email: "", phone: "", service: "" });
    } catch {
      onToast?.({
        message: "Could not submit just now. Please call us on +91 88502 85886.",
        type: "error",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="vh-hero">
      {/* The photograph sits behind a scrim rather than under a flat white
          wash, so it still reads as a photograph. */}
      <div className="vh-hero__media" aria-hidden="true">
        <img
          src="/hero_lifestyle_2026.webp"
          alt=""
          fetchpriority="high"
          decoding="async"
        />
        <div className="vh-hero__scrim" />
      </div>

      <div className="ve-container vh-hero__inner">
        <div className="vh-hero__copy">
          <span className="vh-hero__badge">
            <FaMapMarkerAlt />
            Andheri West, Mumbai
            <i />
            Trusted since 1989
          </span>

          <h1 className="ve-display vh-hero__title">
            <span className="vh-hero__title-top">Your gateway to</span>
            <span className="vh-hero__rotator-slot">
              <AnimatePresence mode="wait">
                <motion.span
                  key={serviceIndex}
                  initial={{ opacity: 0, y: 8, filter: "blur(2px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0, y: -8, filter: "blur(2px)" }}
                  transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                  className="vh-hero__dynamic-highlight"
                >
                  {ROTATING_SERVICES[serviceIndex]}
                </motion.span>
              </AnimatePresence>
            </span>
            <span className="vh-hero__title-bottom">and a life abroad.</span>
          </h1>

          <p className="ve-lede vh-hero__lede">
            One-on-one counselling from the first sitting to the day you land.
            Country and university selection, your SOP, PR pathways, education
            loans and visa filing — with one advisor who stays on the file the
            whole way through.
          </p>

          {/* Departure-board line: the movement the old rotating headline had,
              moved somewhere it does not damage the h1. */}
          <div className="vh-board" aria-live="off">
            <span className="vh-board__label">Now advising on</span>
            <span className="vh-board__slot">
              <AnimatePresence mode="wait">
                <motion.span
                  key={rotating}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                  className="vh-board__value"
                >
                  {NOW_ADVISING[rotating]}
                </motion.span>
              </AnimatePresence>
            </span>
          </div>

          <div className="vh-hero__ctas">
            <a href="#assess" className="ve-btn ve-btn--primary">
              Check your eligibility <FaArrowRight />
            </a>
            <Link to="/about" className="ve-btn ve-btn--ghost">
              Meet the counsellors
            </Link>
          </div>

          <dl className="vh-trust">
            {TRUST.map((t) => (
              <div key={t.label} className="vh-trust__item">
                <dt>{t.figure}</dt>
                <dd>{t.label}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/* ---------------- Assessment card ---------------- */}
        <div className="vh-card" id="assess">
          <div className="vh-card__head">
            <span className="vh-card__tag">Free profile check</span>
            <h2 className="vh-card__title">Find out where you stand</h2>
            <p className="vh-card__sub">
              Tell us four things. A senior counsellor reads your profile and
              calls you back within 24 hours — no fee, no obligation.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="vh-form">
            <label className="vh-field">
              <span className="vh-field__label">
                <FaUser /> Full name
              </span>
              <input
                type="text"
                name="name"
                placeholder="Rahul Sharma"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </label>

            <label className="vh-field">
              <span className="vh-field__label">
                <FaEnvelope /> Email
              </span>
              <input
                type="email"
                name="email"
                placeholder="rahul@example.com"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </label>

            <label className="vh-field">
              <span className="vh-field__label">
                <FaPhone /> Phone
              </span>
              <input
                type="tel"
                name="phone"
                placeholder="+91 98765 43210"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </label>

            <label className="vh-field">
              <span className="vh-field__label">
                <FaClipboardList /> What are you planning?
              </span>
              <select
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
              >
                <option value="">Select a pathway</option>
                <option value="Study Overseas">
                  Study overseas (Bachelors / Masters)
                </option>
                <option value="Immigration">Canada PR &amp; Express Entry</option>
                <option value="Work abroad">Work abroad &amp; job seeker visa</option>
                <option value="Visa services">Visa documentation &amp; IELTS</option>
              </select>
            </label>

            <button
              type="submit"
              className="ve-btn ve-btn--mint vh-form__submit"
              disabled={sending}
            >
              {sending ? "Sending…" : "Get my free assessment"}
              {!sending && <FaArrowRight />}
            </button>

            <p className="vh-form__note">
              We never sell your details or pass them to universities without
              your say-so.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
